import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ref, listAll, getDownloadURL, getMetadata } from "firebase/storage";
import { onAuthStateChanged, type User } from "firebase/auth";
import { storage, auth } from "@/lib/firebase";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lock, FileText, Download, Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

interface DealRoomFile {
  name: string;
  url: string;
  size: number;
  contentType: string;
}

// ─────────────────────────────────────────────────────────────────────────
// CORRECTIF (8 août 2026) : ce composant ne réplique plus la condition de
// storage.rules côté client (isDealParticipant()) — trop risqué de
// désynchronisation si les règles changent. À la place, on tente
// directement listAll() sur deal-room/{dealId}/ ; Storage lui-même fait
// autorité. Un refus (storage/unauthorized) = accès non débloqué, sans
// avoir à deviner pourquoi (NDA non signé, dealParticipants absent, etc.).
// La vraie condition d'accès est dealParticipants/{dealId}_{uid} — créé
// par saveSignedNDA à la signature (voir ce fichier, correctif du même jour).
// ─────────────────────────────────────────────────────────────────────────

export default function DealRoom() {
  const { dealId } = useParams<{ dealId: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === "fr";

 const [checkingAccess, setCheckingAccess] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [files, setFiles] = useState<DealRoomFile[]>([]);
  const [authUser, setAuthUser] = useState<User | null>(null);
  const [authResolved, setAuthResolved] = useState(false);

  // Attend que Firebase Auth ait fini de restaurer la session (asynchrone
  // après un rechargement de page) avant de trancher sur l'accès. Lire
  // auth.currentUser de façon synchrone au montage causait un faux
  // "Connectez-vous" pour un utilisateur pourtant bien connecté, chaque
  // fois que la page se chargeait fraîchement (ex. bouton staff "Voir le
  // Deal Room" qui ouvre un nouvel onglet, ou lien direct par email).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setAuthUser(u);
      setAuthResolved(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (dealId && authResolved) loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, authResolved, authUser]);

  const loadFiles = async () => {
    if (!dealId || !authUser) {
      setCheckingAccess(false);
      return;
    }
    setCheckingAccess(true);
    try {
      const roomRef = ref(storage, `deal-room/${dealId}`);
      const listing = await listAll(roomRef);
      setHasAccess(true);
      const results = await Promise.all(
        listing.items.map(async (item) => {
          const [url, metadata] = await Promise.all([getDownloadURL(item), getMetadata(item)]);
          return {
            name: item.name,
            url,
            size: metadata.size,
            contentType: metadata.contentType || "application/octet-stream",
          };
        }),
      );
      setFiles(results);
    } catch (err: any) {
      // storage/unauthorized (ou codes équivalents) = accès non débloqué,
      // ce qui est un état normal/attendu, pas une erreur à logger en rouge.
      if (err?.code === "storage/unauthorized") {
        setHasAccess(false);
      } else {
        console.error("[DealRoom] Unexpected error:", err);
        setHasAccess(false);
      }
    } finally {
      setCheckingAccess(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <Lock className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">
          {isFr ? "Connectez-vous pour accéder au Deal Room." : "Sign in to access the Deal Room."}
        </p>
        <Button onClick={() => navigate("/login")}>{isFr ? "Se connecter" : "Sign In"}</Button>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <Lock className="h-12 w-12 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-bold mb-1">
            {isFr ? "Accès restreint" : "Restricted Access"}
          </h1>
          <p className="text-muted-foreground max-w-md">
            {isFr
              ? "Ce Deal Room contient des documents confidentiels. Signez le NDA de ce deal pour y accéder."
              : "This Deal Room contains confidential documents. Sign this deal's NDA to gain access."}
          </p>
        </div>
        <Button onClick={() => navigate(`/tools/nda-generator?deal=${dealId}&recipientType=buyer`)}>
          {isFr ? "Signer le NDA" : "Sign NDA"}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        {isFr ? "Retour" : "Back"}
      </Button>

      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-green-600" />
        <h1 className="text-2xl font-bold">{isFr ? "Deal Room" : "Deal Room"}</h1>
      </div>
      <p className="text-sm text-muted-foreground -mt-4">
        {isFr
          ? "Documents confidentiels partagés par l'équipe AcquizGroup pour ce deal."
          : "Confidential documents shared by the AcquizGroup team for this deal."}
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isFr ? "Documents disponibles" : "Available Documents"} ({files.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {files.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {isFr
                ? "Aucun document partagé pour le moment. Revenez plus tard."
                : "No documents shared yet. Check back later."}
            </p>
          ) : (
            <div className="space-y-2">
              {files.map((file) => (
                <a
                  key={file.name}
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatSize(file.size)}</p>
                    </div>
                  </div>
                  <Download className="h-4 w-4 text-muted-foreground shrink-0" />
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}